import DeterministicIdGenerator from '@/utils/DeterministicIdGenerator';
import { ExcelProcessorUtils } from '@/utils/ExcelProcessorUtils';
import { calculateWorkingMinutesBetweenMs, resolvePublishedCalendar } from '@/utils/CalendarEndDateCalculator';
import { parseP6Relationships } from '@/utils/P6RelationshipParser';
import { matchP6Calendar } from '@/utils/P6CalendarMatch';
import { P6_TIMEZONE } from '@/utils/p6TimeZone';
import { childText, childElement } from '@/utils/XmlDomUtils';
import { measure } from '@/utils/perf';
import { forEachChunked, yieldToMain } from '@/utils/mainThreadScheduler';
import { toRaw } from 'vue';
import { useScheduleStore } from '@/stores/schedule';
import { useTaskStore } from '@/stores/task';

// Lock key stamped on every task imported from P6. Tasks with a key are
// structure-locked in RDrive, so P6-sourced tasks can't be manually edited
// there; commits from this app must include the key in the commit's `keys`
// list to be allowed to update them (see ScheduleImporter).
export const P6_TASK_KEY = 'p6';

// P6 XML exports timezone-naive timestamps ("2026-05-25T07:00:00"); they are
// parsed in the backend's host zone and the imported P6 calendars are stamped
// with the same zone — see p6TimeZone.js for why both must agree.
export const P6_DATE_TIMEZONE = P6_TIMEZONE.iana;

// UTC offset (ms) of `timeZone` at the moment `utcMs`.
function zoneOffsetMs(utcMs, timeZone) {
  const parts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }).formatToParts(new Date(utcMs)).forEach(p => { parts[p.type] = p.value; });
  const wallAsUtc = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    parts.hour === '24' ? 0 : Number(parts.hour), Number(parts.minute), Number(parts.second)
  );
  return wallAsUtc - utcMs;
}

/**
 * Parse a P6 date string. Timezone-naive strings are interpreted as wall time
 * in P6_DATE_TIMEZONE (matching the backend); strings that carry an explicit
 * offset/Z are parsed as-is. Returns null for missing input.
 */
export function parseP6Date(dateString) {
  if (!dateString) return null;
  if (dateString instanceof Date) return dateString;

  const naive = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/.exec(dateString.trim());
  if (!naive) return new Date(dateString);

  const [year, month, day, hour, minute, second] = naive.slice(1).map(Number);
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  let offset = zoneOffsetMs(wallAsUtc, P6_DATE_TIMEZONE);
  // Re-derive once in case the first guess straddled a DST transition.
  const refined = zoneOffsetMs(wallAsUtc - offset, P6_DATE_TIMEZONE);
  if (refined !== offset) offset = refined;
  return new Date(wallAsUtc - offset);
}

// Deterministic RDrive calendar code for a P6 calendar name. This is the
// create-vs-update matching key for imported calendars (see CalendarImporter),
// so it must stay stable for a given name across imports.
export function generateCalendarCode(name) {
  return (name || '')
    .replace(/[^a-zA-Z0-9\-_]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50) || 'p6-calendar';
}

// --- P6 calendar work periods -------------------------------------------------
//
// RDrive interval priorities: the weekly pattern, then a whole exception day
// taken out of it, then the working hours an exception restores on that day.
const WORK_WEEK_PRIORITY = 20;
const EXCEPTION_PRIORITY = 30;
const EXCEPTION_WORK_PRIORITY = 40;
const MINUTES_PER_DAY = 24 * 60;
// P6's built-in work day for a calendar edited in "Standard" (hours per day
// only) mode: it starts at 08:00 and runs HoursPerDay hours.
const STANDARD_WORK_DAY_START_MINUTES = 8 * 60;
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// One minute: the imported working time is counted on a minute grid.
const DURATION_CHECK_TOLERANCE_HOURS = 1 / 60 + 1e-9;

function p6TimeToMinutes(text) {
  const [hours, minutes] = String(text || '').split(':').map(Number);
  return (Number.isFinite(hours) ? hours : 0) * 60 + (Number.isFinite(minutes) ? minutes : 0);
}

function formatMinutes(minutes) {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function isoDay(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * A P6 <WorkTime> as minutes since midnight, { startMin, endMin } with an
 * EXCLUSIVE end, or null when the element carries no period (P6 writes
 * `<WorkTime xsi:nil="true"/>` for a non-working day).
 *
 * P6 finish times are inclusive minutes: "Finish 16:59:00" means the period
 * runs through the end of that minute, so 07:00-16:59 is P6's ten-hour day
 * and 08:00-11:59 plus 13:00-16:59 its eight-hour day. RDrive intervals,
 * the API's walk and Bryntum all treat the end as exclusive, so the finish
 * is advanced one minute; without that every day is a minute short and a
 * P6 finish at the end of the day sits just outside the working window.
 */
export function parseP6WorkTime(workTimeNode) {
  const start = childText(workTimeNode, 'Start');
  const finish = childText(workTimeNode, 'Finish');
  if (!start || !finish) return null;
  const startMin = p6TimeToMinutes(start);
  const endMin = Math.min(p6TimeToMinutes(finish) + 1, MINUTES_PER_DAY);
  if (endMin <= startMin) return null;
  return { startMin, endMin };
}

function cronAt(minutes, dayNum) {
  return `${minutes % 60} ${Math.floor(minutes / 60)} * * ${dayNum}`;
}

function nonWorkingDayInterval(dayOfWeek, dayNum) {
  return {
    title: `${dayOfWeek} (non-working)`,
    recurrentStartDate: `0 0 * * ${dayNum}`,
    recurrentEndDate: `0 0 * * ${dayNum}`,
    isWorking: false,
    priority: WORK_WEEK_PRIORITY
  };
}

// A recurring end must stay on the same day as its start (the API requires
// matching day patterns), so a period running to midnight ends at 23:59.
function recurringWorkInterval(title, dayNum, { startMin, endMin }) {
  return {
    title,
    recurrentStartDate: cronAt(startMin, dayNum),
    recurrentEndDate: cronAt(Math.min(endMin, MINUTES_PER_DAY - 1), dayNum),
    isWorking: true,
    priority: WORK_WEEK_PRIORITY
  };
}

/**
 * Parse a P6 XML export into a DOM Document. DOMParser.parseFromString is
 * atomic and blocks the main thread for seconds on large exports, so callers
 * that need the document more than once (calendar pre-scan at file selection,
 * then the full task parse) should parse once and pass the Document on.
 *
 * @param {string} xmlText - Raw XML file contents
 * @returns {Document}
 */
export function parseP6XmlDocument(xmlText) {
  return new DOMParser().parseFromString(xmlText, 'text/xml');
}

/**
 * Lightweight scan of a P6 XML export for its calendars, without running the
 * full task parse. Used at file-selection time to pre-resolve the schedule's
 * calendar-per-time-range mapping before the user continues.
 *
 * @param {string|Document} xmlSource - Raw XML file contents, or an already
 *   parsed Document (see parseP6XmlDocument) to avoid a second DOM parse
 * @returns {{ calendars: Array<{p6ObjectId: string, title: string, code: string}>,
 *             defaultCalendar: {p6ObjectId: string, title: string, code: string}|null }}
 *   defaultCalendar is the project's ActivityDefaultCalendarObjectId target,
 *   falling back to the calendar most referenced by activities.
 */
export function extractP6CalendarSummary(xmlSource) {
  const xmlDoc = typeof xmlSource === 'string' ? parseP6XmlDocument(xmlSource) : xmlSource;

  const calendars = Array.from(xmlDoc.getElementsByTagName('Calendar')).map(calNode => {
    const title = childText(calNode, 'Name') || 'Unnamed Calendar';
    return {
      p6ObjectId: childText(calNode, 'ObjectId') || '',
      title,
      code: generateCalendarCode(title)
    };
  });

  const byObjectId = new Map(calendars.map(c => [c.p6ObjectId, c]));

  const projectNode = xmlDoc.getElementsByTagName('Project')[0];
  const defaultObjectId = projectNode && childText(projectNode, 'ActivityDefaultCalendarObjectId');
  let defaultCalendar = defaultObjectId ? byObjectId.get(defaultObjectId) || null : null;

  if (!defaultCalendar) {
    const usage = new Map();
    Array.from(xmlDoc.getElementsByTagName('Activity')).forEach(activity => {
      const id = childText(activity, 'CalendarObjectId');
      if (id && byObjectId.has(id)) usage.set(id, (usage.get(id) || 0) + 1);
    });
    let bestId = null;
    usage.forEach((count, id) => {
      if (bestId === null || count > usage.get(bestId)) bestId = id;
    });
    defaultCalendar = bestId ? byObjectId.get(bestId) : null;
  }

  return { calendars, defaultCalendar };
}

/**
 * Find the published RDrive calendar a P6 calendar corresponds to — by its
 * persisted p6-id tag first (rename-proof), then by its deterministic code
 * (case-insensitive) — the same precedence CalendarImporter uses for
 * create-vs-update. Returns null when no calendar matches (e.g. the P6
 * calendars have not been imported into the project yet).
 */
export function matchP6CalendarByCode(p6Calendar, publishedCalendars) {
  return matchP6Calendar(p6Calendar, publishedCalendars)?.calendar || null;
}

export class XMLTaskParser {
    constructor(selectedSchedule, selectedTimeRangeTemplate) {
      this.scheduleStore = useScheduleStore();
      this.taskStore = useTaskStore();
      this.selectedSchedule = selectedSchedule;
      this.selectedTimeRangeTemplate = selectedTimeRangeTemplate;
      this.flatTasks = new Map();
      this.idGenerator = new DeterministicIdGenerator();
      this.idMapping = new Map();
      // Maps the (new) task id -> P6 Calendar ObjectId the activity referenced.
      // Kept as side-metadata so it never leaks into the committed task payload.
      this.taskCalendarLinks = new Map();
      // toRaw: existing tasks are read field-by-field for every parsed node;
      // raw objects skip the reactive proxy's per-access overhead.
      this.existingTasksById = this.createTaskMap(toRaw(this.taskStore.getTasks));
      this.excelUtils = new ExcelProcessorUtils(selectedSchedule, selectedTimeRangeTemplate);
    }

    createTaskMap(tasks) {
      const taskMap = {};
      
      function addTaskToMap(task) {
        taskMap[task.id] = task;
        if (task.children && task.children.length > 0) {
          task.children.forEach(childTask => addTaskToMap(childTask));
        }
      }
      
      tasks.forEach(task => addTaskToMap(task));
      return taskMap;
    }
  
    static DEFAULT_HOURS_PER_DAY = 8;

    /**
     * Duration of a P6 activity: the WORKING time between its StartDate and
     * FinishDate on `calendar` (the activity's own P6 calendar when the file
     * has it, else the published calendar the range is committed against),
     * as working HOURS (see workingDuration). The main web app's gantt shows
     * the same quantity because it derives duration from the dates on that
     * calendar.
     *
     * Without a usable calendar the span is the elapsed 24h time: a tracking
     * range with no mapped calendar is committed against the seed 24/7
     * default calendar (MMCAL247), on which working time IS elapsed time.
     */
    calculateDuration(startDateStr, endDateStr, calendar = null) {
      if (!startDateStr || !endDateStr) return null;

      const startDate = parseP6Date(startDateStr);
      const endDate = parseP6Date(endDateStr);

      const diffMs = endDate - startDate;

      // A zero span (StartDate === FinishDate) is a milestone and keeps a
      // genuine zero duration. P6 exports can also have FinishDate before
      // StartDate; clamp that corrupt case to zero as well, since a negative
      // duration/weighting is rejected by the backend.
      if (diffMs <= 0) {
        return { value: 0, unit: 'day', weighting: 0 };
      }

      if (calendar) {
        const minutes = calculateWorkingMinutesBetweenMs(calendar, startDate.getTime(), endDate.getTime());
        if (minutes) return XMLTaskParser.workingDuration(minutes / 60);
      }

      return XMLTaskParser.workingDuration(diffMs / (1000 * 60 * 60));
    }

    /**
     * The committed endDate for a P6 activity: its FinishDate, except that a
     * FinishDate before the StartDate (P6 exports contain these — e.g. a
     * constrained start pushed past a fixed finish) is clamped to the start.
     * The API rejects StartDate > EndDate, and calculateDuration already
     * treats such a span as a zero-duration milestone at the start.
     */
    static p6EndDate(startDateStr, endDateStr) {
      const start = parseP6Date(startDateStr);
      const end = parseP6Date(endDateStr);
      return (end < start ? start : end).toISOString();
    }

    /**
     * A committed duration object for `hours` of working time. Expressed in
     * hours (minutes below one hour) rather than calendar days so the
     * backend's ToDurationWeightingFromDisplay reproduces the weighting on
     * WHATEVER calendar the range ends up committed against — a 'day' value
     * scaled by one calendar's hoursPerDay would be re-scaled by another's
     * (e.g. 8h P6 days re-read as 24h days on the seed default calendar when
     * the calendar import is skipped). The API re-humanizes value/unit from
     * the weighting on every GET, so the committed unit is never displayed.
     */
    static workingDuration(hours) {
      if (hours < 1) {
        const minutes = Math.round(hours * 60);
        return { value: minutes, unit: 'minute', weighting: minutes / 60 };
      }
      return { value: hours, unit: 'hour', weighting: hours };
    }

    calculateWBSDates(task, spans, existingSpansMemo) {
    if (task.type !== 'wbs') return;

    // Ensure task has timeRanges
    if (!task.timeRanges || !task.timeRanges.length) {
      task.timeRanges = this.getTimeRanges(null);
      if (!task.timeRanges || !task.timeRanges.length) return;
    }

    // Roll up the same spans over the previously stored children. When the
    // two rollups match, this import does not move the parent — keep its
    // stored timeRanges verbatim. The backend normalizes committed parent
    // dates/durations (day-snapped starts, humanized units, reweighted), so
    // re-deriving them client-side would diff as a false change on every
    // re-import of an unchanged file.
    const existingTask = this.existingTasksById[task.id];
    const existingSpans = existingTask?.children?.length
      ? this.collectExistingSpans(existingTask, existingSpansMemo)
      : null;

    // Roll up child dates onto the parent. The client owns
    // duration.value/unit/weighting for the commit, and a null duration fails
    // backend [Required] validation. So always keep a real duration object:
    // derive it from the rolled-up span when both ends are known, otherwise
    // leave the existing (already-populated) default in place.
    task.timeRanges.forEach(timeRange => {
      const data = spans.get(timeRange.timeRangeTemplateId);
      if (!data || !data.minStart) return;

      const previous = existingSpans?.get(timeRange.timeRangeTemplateId);
      const sameStart = previous?.minStart?.getTime() === data.minStart.getTime();
      const sameEnd = (previous?.maxEnd?.getTime() ?? null) === (data.maxEnd?.getTime() ?? null);
      if (previous && sameStart && sameEnd) return;

      timeRange.startDate = data.minStart.toISOString();
      if (data.maxEnd) {
        timeRange.endDate = data.maxEnd.toISOString();
        timeRange.duration = this.calculateWBSDuration(timeRange, data.minStart, data.maxEnd)
          || this.calculateDuration(data.minStart, data.maxEnd);
      }
    });
  }

    /**
     * Duration for a WBS rollup span: the working time between minStart and
     * maxEnd on the parent's committed calendar — what the main web app shows
     * for the parent (its tooltip humanizes the stored weighting for locked
     * P6 tasks). Null (→ calculateDuration's elapsed/24-7 fallback) for
     * non-tracking range types or when the calendar can't be resolved.
     */
    calculateWBSDuration(timeRange, minStart, maxEnd) {
      const type = timeRange.timeRangeTemplateType;
      if (type !== 'calendarTracking' && type !== 'dependencyTracking' && type !== 'timeTracking') {
        return null;
      }

      const calendar = this.resolveWalkCalendar(timeRange);
      if (!calendar) return null;

      const minutes = calculateWorkingMinutesBetweenMs(calendar, minStart.getTime(), maxEnd.getTime());
      if (!minutes) return null;

      return XMLTaskParser.workingDuration(minutes / 60);
    }

    // Merge one task's own timeRanges into a per-templateId span map
    // (earliest start / latest end).
    mergeOwnSpans(task, spans) {
      (task.timeRanges || []).forEach(timeRange => {
        if (!timeRange.timeRangeTemplateId) return;

        const startDate = timeRange.startDate ? new Date(timeRange.startDate) : null;
        const endDate = this.computeTimeRangeEnd(timeRange);
        this.mergeSpan(spans, timeRange.timeRangeTemplateId, startDate, endDate);
      });
    }

    mergeSpan(spans, templateId, minStart, maxEnd) {
      let span = spans.get(templateId);
      if (!span) {
        span = { minStart: null, maxEnd: null };
        spans.set(templateId, span);
      }
      if (minStart && (!span.minStart || minStart < span.minStart)) span.minStart = minStart;
      if (maxEnd && (!span.maxEnd || maxEnd > span.maxEnd)) span.maxEnd = maxEnd;
    }

    // Span map of a stored task's strict descendants, memoised by task id.
    // The stored tree is never mutated during a parse, so each subtree is
    // computed once however many ancestors ask for it.
    collectExistingSpans(task, memo) {
      const cached = memo.get(task.id);
      if (cached) return cached;

      const spans = new Map();
      (task.children || []).forEach(child => {
        this.mergeOwnSpans(child, spans);
        this.collectExistingSpans(child, memo).forEach((span, templateId) =>
          this.mergeSpan(spans, templateId, span.minStart, span.maxEnd));
      });
      memo.set(task.id, spans);
      return spans;
    }

    // Derive a timeRange's end date. Prefer an explicit endDate (P6 ranges
    // always carry their FinishDate); otherwise add the duration to startDate
    // as elapsed time (day = 24h), matching calculateDuration's fallback.
    computeTimeRangeEnd(timeRange) {
      if (timeRange.endDate) return new Date(timeRange.endDate);
      if (!timeRange.startDate || !timeRange.duration || timeRange.duration.value == null) return null;

      const start = new Date(timeRange.startDate);
      const { value, unit } = timeRange.duration;
      const unitMs = { minute: 60 * 1000, hour: 60 * 60 * 1000, day: 24 * 60 * 60 * 1000 };
      const ms = unitMs[unit];
      if (!ms) return null;
      return new Date(start.getTime() + value * ms);
    }

    updateWBSDates(tasks) {
    // Single post-order pass: each node's descendant span map is merged from
    // its children's own timeRanges plus the children's already-computed
    // maps, so every node is visited once. (Previously each WBS ancestor
    // re-walked its entire subtree, which was quadratic on deep exports.)
    // A child is processed — and its WBS dates updated — before its own
    // ranges are merged into the parent's map, preserving the bottom-up
    // rollup-of-rollups the re-walk produced.
    const existingSpansMemo = new Map();

    const processTask = (task) => {
      const spans = new Map();
      task.children.forEach(child => {
        const childSpans = processTask(child);
        this.mergeOwnSpans(child, spans);
        childSpans.forEach((span, templateId) =>
          this.mergeSpan(spans, templateId, span.minStart, span.maxEnd));
      });

      if (task.children.length > 0) {
        this.calculateWBSDates(task, spans, existingSpansMemo);
      }
      return spans;
    };

    tasks.forEach(processTask);
  }

    /**
     * Async so the node loops can yield to the event loop between time
     * slices (see mainThreadScheduler) — a large export otherwise blocks the
     * main thread long enough for the browser's "page is slowing down"
     * banner. onProgress({ stage, done, total }) reports slice progress.
     */
    async parse(xmlDoc, { onProgress } = {}) {
      this.idMapping.clear();
      this.flatTasks.clear();
      this.taskCalendarLinks.clear();
      this.durationCheck = {
        compared: 0,
        matched: 0,
        mismatched: 0,
        skippedInProgress: 0,
        byCalendar: new Map(),
        samples: []
      };
      // Fresh generator per parse: its collision-salting depends on the set of
      // ids already handed out, so reusing it across parses would make the
      // same seed yield different ids.
      this.idGenerator = new DeterministicIdGenerator();
      this.calendars = measure('xml-parse-calendars', () => this.parseCalendars(xmlDoc));
      this.calendarsByP6Id = new Map(this.calendars.map(calendar => [calendar.p6ObjectId, calendar]));

      const wbsNodes = Array.from(xmlDoc.getElementsByTagName('WBS'));
      const activities = Array.from(xmlDoc.getElementsByTagName('Activity'));
      const totalNodes = wbsNodes.length + activities.length;
      const nodesProgress = (done) => {
        if (onProgress) onProgress({ stage: 'tasks', done, total: totalNodes });
      };
      await measure('xml-parse-wbs', () =>
        this.parseWBSNodes(wbsNodes, nodesProgress));
      await measure('xml-parse-activities', () =>
        this.parseActivities(activities, (done) => nodesProgress(wbsNodes.length + done)));
      // Resolve P6 relationships onto the parsed tasks. Only edges RDrive can
      // represent faithfully (finish-to-start, zero lag) become
      // prerequisiteTaskIds; the summary counts everything dropped so the
      // review step can show the user what was not imported.
      const { prerequisitesByTaskId, summary: relationshipSummary } =
        await measure('xml-parse-relationships', () => parseP6Relationships(xmlDoc, this.idMapping, { onProgress }));
      this.flatTasks.forEach(task => {
        const prerequisites = prerequisitesByTaskId.get(task.id);
        if (prerequisites && prerequisites.size) {
          task.prerequisiteTaskIds = Array.from(prerequisites);
        }
      });

      // The tree passes below are single-pass/linear but still walk every
      // task; give the event loop a turn before and between them.
      if (onProgress) onProgress({ stage: 'hierarchy' });
      await yieldToMain();
      const tasks = measure('xml-build-hierarchy', () => this.buildHierarchy());
      const prunedEmptyWbsCount = this.pruneEmptyWbsBranches(tasks);
      await yieldToMain();
      measure('xml-wbs-rollup', () => this.updateWBSDates(tasks));
      // Prune calendars no activity references. P6 exports every calendar the
      // project defines (plus resource-only global calendars the task manager
      // doesn't use); importing them all would clutter RDrive with unused
      // entities. A calendar dropped here that a future export does use is
      // simply created by that import (create-vs-update matches by code).
      const usedCalendarIds = new Set(this.taskCalendarLinks.values());
      this.calendars = this.calendars.filter(c => usedCalendarIds.has(c.p6ObjectId));
      // taskCalendarLinks is returned as side-metadata (task id -> P6 calendar
      // ObjectId). It is used after the P6 calendars are created/updated to
      // rewrite the tasks' timeRanges[].calendarId, but is never committed.
      return {
        tasks,
        calendars: this.calendars,
        taskCalendarLinks: this.taskCalendarLinks,
        relationshipSummary,
        // How many WBS nodes were dropped for having no activities beneath
        // them; shown on the review step. Never sent in the commit payload.
        prunedEmptyWbsCount,
        // How the committed working-time durations compare with the durations
        // P6 carries for the activities (see recordDurationCheck); shown on
        // the review step. Never sent in the commit payload.
        durationSummary: {
          compared: this.durationCheck.compared,
          matched: this.durationCheck.matched,
          mismatched: this.durationCheck.mismatched,
          skippedInProgress: this.durationCheck.skippedInProgress,
          calendars: Array.from(this.durationCheck.byCalendar.values())
            .sort((a, b) => b.mismatched - a.mismatched || b.total - a.total),
          samples: this.durationCheck.samples
        }
      };
  }

    // Drop WBS branches that contain no activities. P6 masters routinely
    // carry placeholder WBS buckets for not-yet-detailed scope; they have no
    // dates of their own in the XML (WBS dates exist only as activity
    // rollups), so importing them would commit tracking time ranges with
    // startDate: null, which the API rejects. A future export that populates
    // such a bucket simply creates it then — ids are deterministic, so
    // re-imports still match. Returns the number of WBS nodes removed.
    pruneEmptyWbsBranches(tasks) {
      let pruned = 0;

      const pruneLevel = (list) => list.filter(task => {
        if (task.type !== 'wbs') return true;

        task.children = pruneLevel(task.children || []);
        // childTaskIds mirrors children (see buildHierarchy); keep it in sync
        // so pruned ids never reach the commit payload.
        if (task.childTaskIds) {
          task.childTaskIds = task.children.map(child => child.id);
        }

        // Children are already pruned, so any survivor's subtree contains an
        // activity; a WBS left childless holds none and is dropped.
        if (task.children.length > 0) return true;
        pruned += 1;
        return false;
      });

      const kept = pruneLevel(tasks);
      tasks.length = 0;
      tasks.push(...kept);
      return pruned;
    }

    parseCalendars(xmlDoc) {
      const calendarNodes = xmlDoc.getElementsByTagName('Calendar');
      if (!calendarNodes.length) return [];

      return Array.from(calendarNodes).map(calNode => {
        const objectId = childText(calNode, 'ObjectId') || '';
        const name = childText(calNode, 'Name') || 'Unnamed Calendar';
        const hoursPerDay = parseFloat(childText(calNode, 'HoursPerDay')) || 8;
        // P6's own unit-conversion factors. The backend, the main web app's
        // gantt and this app all read a "week"/"month"/"year" as daysPerWeek /
        // daysPerMonth / 12 x daysPerMonth working days of hoursPerDay hours,
        // so a 7-day 10h calendar (70h/week, 300h/month in P6) must carry
        // 7 d/wk and 30 d/mo — with the old fixed 5/20, its 3,600-hour year
        // shrank to 2,400h and every long span read ~50% too long.
        const hoursPerWeek = parseFloat(childText(calNode, 'HoursPerWeek'));
        const hoursPerMonth = parseFloat(childText(calNode, 'HoursPerMonth'));
        const round2 = (n) => Math.round(n * 100) / 100;
        const daysPerWeek = hoursPerWeek > 0 ? round2(hoursPerWeek / hoursPerDay) : 5;
        const daysPerMonth = hoursPerMonth > 0 ? round2(hoursPerMonth / hoursPerDay) : 20;
        const code = this.generateCalendarCode(name);

        const nonWorkingDays = [];
        const workingWindows = [];

        // Parse StandardWorkWeek. A day without a usable work period — no
        // <WorkTime>, or the `<WorkTime xsi:nil="true"/>` P6 writes for a
        // non-working day — becomes an explicit non-working day.
        const workWeek = childElement(calNode, 'StandardWorkWeek');
        if (workWeek) {
          Array.from(workWeek.getElementsByTagName('StandardWorkHours')).forEach(dayNode => {
            const dayOfWeek = childText(dayNode, 'DayOfWeek');
            const dayNum = DAY_NAMES.indexOf(dayOfWeek);
            if (dayNum < 0) return;

            const windows = Array.from(dayNode.getElementsByTagName('WorkTime'))
              .map(parseP6WorkTime)
              .filter(Boolean);
            if (!windows.length) {
              nonWorkingDays.push(nonWorkingDayInterval(dayOfWeek, dayNum));
              return;
            }
            windows.forEach(window =>
              workingWindows.push(recurringWorkInterval(`${dayOfWeek} working hours`, dayNum, window)));
          });
        }

        // A calendar whose export has no work period on any day is one P6
        // keeps in its "Standard" work-week mode (hours per day only, no
        // detailed hours): the exporter writes every day as nil, while P6
        // schedules the calendar with its built-in day, Monday to Friday
        // from 08:00 for HoursPerDay hours. Publishing the export literally
        // would give the calendar no working time at all, which turns every
        // duration on it into elapsed time. HoursPerWeek is deliberately not
        // consulted: it is a duration-conversion factor the planner types
        // and can describe a week P6 does not actually work. The assumption
        // is surfaced on the calendar so the planner can confirm it.
        let assumedWorkWeek = null;
        let intervals;
        if (workingWindows.length) {
          intervals = [...nonWorkingDays, ...workingWindows];
        } else {
          const window = {
            startMin: STANDARD_WORK_DAY_START_MINUTES,
            endMin: Math.min(STANDARD_WORK_DAY_START_MINUTES + Math.round(hoursPerDay * 60), MINUTES_PER_DAY)
          };
          assumedWorkWeek = `Monday to Friday, ${formatMinutes(window.startMin)} to ${formatMinutes(Math.min(window.endMin, MINUTES_PER_DAY - 1))}`;
          intervals = [
            ...[0, 6].map(dayNum => nonWorkingDayInterval(DAY_NAMES[dayNum], dayNum)),
            ...[1, 2, 3, 4, 5].map(dayNum =>
              recurringWorkInterval(`${DAY_NAMES[dayNum]} working hours (assumed)`, dayNum, window))
          ];
        }

        // Parse HolidayOrExceptions. The naive P6 date is a calendar DAY in
        // the calendar's own zone (the P6 timezone frame the calendar is
        // stamped with), so the all-day exclusion runs from that zone's
        // midnight to the next — the API validates all-day interval dates
        // against the calendar's zone, and a browser-local parse would shift
        // the holiday by the user's UTC offset. Both bounds go through
        // parseP6Date so a DST change inside the day keeps the end at the
        // next local midnight.
        // An exception replaces the day's standard hours: the whole day is
        // taken out of the work week, then any work periods the exception
        // carries (a reduced-hours day, a working weekend) are restored on
        // top of that at a higher priority. P6 applies only the calendar's
        // own exceptions, not those of its base calendar.
        // Nested under a <HolidayOrExceptions> wrapper, so this must be a
        // descendant lookup, not a direct-child scan.
        const exceptions = calNode.getElementsByTagName('HolidayOrException');
        Array.from(exceptions).forEach(exc => {
          const excName = childText(exc, 'Name') || 'Exception';
          const excDate = childText(exc, 'Date');
          if (!excDate) return;

          const dayMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(excDate.trim());
          if (!dayMatch) return;
          const [year, month, day] = dayMatch.slice(1).map(Number);
          const nextDay = new Date(Date.UTC(year, month - 1, day + 1));
          const dayIso = isoDay(year, month, day);
          const nextDayIso = isoDay(nextDay.getUTCFullYear(), nextDay.getUTCMonth() + 1, nextDay.getUTCDate());
          const dayStart = parseP6Date(`${dayIso}T00:00:00`).toISOString();
          const dayEnd = parseP6Date(`${nextDayIso}T00:00:00`).toISOString();

          intervals.push({
            title: excName,
            startDate: dayStart,
            endDate: dayEnd,
            isWorking: false,
            priority: EXCEPTION_PRIORITY
          });

          Array.from(exc.getElementsByTagName('WorkTime'))
            .map(parseP6WorkTime)
            .filter(Boolean)
            .forEach(({ startMin, endMin }) => {
              intervals.push({
                title: `${excName} working hours`,
                startDate: parseP6Date(`${dayIso}T${formatMinutes(startMin)}:00`).toISOString(),
                endDate: endMin >= MINUTES_PER_DAY
                  ? dayEnd
                  : parseP6Date(`${dayIso}T${formatMinutes(endMin)}:00`).toISOString(),
                isWorking: true,
                priority: EXCEPTION_WORK_PRIORITY
              });
            });
        });

        return {
          p6ObjectId: objectId,
          title: name,
          code,
          hoursPerDay,
          daysPerWeek,
          daysPerMonth,
          // The working windows above are wall-clock times in the zone the
          // activity timestamps are parsed in (see p6TimeZone.js).
          timeZone: P6_TIMEZONE.windowsId,
          tags: ['p6-import'],
          intervals,
          // Set when the export carried no work periods and the work week
          // above is P6's Standard-mode default rather than exported data.
          // Shown in the calendar import dialog; never sent to the API.
          assumedWorkWeek
        };
      });
    }

    generateCalendarCode(name) {
      return generateCalendarCode(name);
    }

    parseWBSNodes(wbsNodes, onNodeProgress) {
      return forEachChunked(wbsNodes, node => {
        // `?? undefined` keeps querySelector's missing-element result: the
        // deterministic id seed and the committed payload both distinguish
        // undefined from null.
        const originalId = childText(node, 'ObjectId') ?? undefined;
        const originalParentTaskId = childText(node, 'ParentObjectId') || null;
        const newId = this.generateNewId('wbs', originalId);
        this.idMapping.set(originalId, newId);

        // Get the sequence number from P6 XML export for WBS nodes
        const sequenceNumber = parseInt(childText(node, 'SequenceNumber'), 10) || 0;

        // Try to get start and end dates from the XML if they exist
        const startDate = childText(node, 'StartDate');
        const endDate = childText(node, 'FinishDate');
        
        // We use existing task only to copy over certain properties, not to include it in the hierarchy
        const existingTask = this.existingTasksById[newId];
        
        this.flatTasks.set(originalId, {
          id: newId,
          parentTaskId: originalParentTaskId, // Will be updated in buildHierarchy
          code: childText(node, 'Code') ?? undefined,
          title: childText(node, 'Name') ?? undefined,
          type: 'wbs',
          key: P6_TASK_KEY,
          timeRanges: this.getTimeRanges(existingTask, startDate, endDate),
          scheduleId: this.selectedSchedule.id,
          children: [], // Start with empty children array - will be populated in buildHierarchy
          subtasks: existingTask?.subtasks || [],
          childTaskIds: [], // Start with empty childTaskIds - will be populated in buildHierarchy
          prerequisiteTaskIds: existingTask?.prerequisiteTaskIds || [],
          labels: existingTask?.labels || [],
          tags: existingTask?.tags || [],
          meta: existingTask?.meta || {},
          sequenceNumber: sequenceNumber // Store the sequence number for sorting
        });
      }, { onProgress: onNodeProgress });
    }

    parseActivities(activities, onNodeProgress) {
      return forEachChunked(activities, activity => {
        const originalId = childText(activity, 'ObjectId') ?? undefined;
        const originalParentTaskId = childText(activity, 'WBSObjectId') ?? undefined;
        const newId = this.generateNewId('activity', originalId);
        this.idMapping.set(originalId, newId);

        const startDate = childText(activity, 'StartDate');
        const endDate = childText(activity, 'FinishDate');

        // Preserve which P6 calendar this activity used (by P6 ObjectId). This is
        // stored as side-metadata only — never on the committed task payload.
        const calendarObjectId = childText(activity, 'CalendarObjectId');
        if (calendarObjectId) {
          this.taskCalendarLinks.set(newId, calendarObjectId);
        }

        const existingTask = this.existingTasksById[newId];
        const timeRanges = this.getTimeRanges(existingTask, startDate, endDate, calendarObjectId);
        this.recordDurationCheck(activity, timeRanges, calendarObjectId);

        this.flatTasks.set(originalId, {
          id: newId,
          parentTaskId: originalParentTaskId,
          code: childText(activity, 'Id') ?? undefined,
          title: childText(activity, 'Name') ?? undefined,
          type: 'activity',
          key: P6_TASK_KEY,
          children: [],
          timeRanges,
          scheduleId: this.selectedSchedule.id,
          subtasks: existingTask?.subtasks || [],
          prerequisiteTaskIds: existingTask?.prerequisiteTaskIds || [],
          labels: existingTask?.labels || [],
          tags: existingTask?.tags || []
        });
      }, { onProgress: onNodeProgress });
    }

    /**
     * Compare the working-time duration this import commits for an activity
     * with the duration P6 itself carries for it: ActualDuration +
     * RemainingDuration, in hours. The two are equal exactly when the parsed
     * calendar reproduces P6's working time between the activity's dates, so
     * a run of mismatches on one calendar means its work week or exceptions
     * did not survive the export (see parseCalendars' assumed work week) and
     * the activities will not walk back to their P6 finish on it. In-progress
     * activities are not compared: P6 records their actual duration
     * independently of the calendar time elapsed since the actual start.
     */
    recordDurationCheck(activity, timeRanges, calendarObjectId) {
      const check = this.durationCheck;
      if (!check) return;

      const actual = parseFloat(childText(activity, 'ActualDuration'));
      const remaining = parseFloat(childText(activity, 'RemainingDuration'));
      if (!Number.isFinite(actual) && !Number.isFinite(remaining)) return;
      const p6Hours = (Number.isFinite(actual) ? actual : 0) + (Number.isFinite(remaining) ? remaining : 0);

      const range = (timeRanges || []).find(tr =>
        tr.timeRangeTemplateType !== 'none' && Number.isFinite(tr.duration?.weighting));
      if (!range) return;

      if ((childText(activity, 'Status') || '') === 'In Progress') {
        check.skippedInProgress += 1;
        return;
      }

      const calendar = (calendarObjectId && this.calendarsByP6Id?.get(calendarObjectId)) || null;
      const key = calendarObjectId || '';
      let entry = check.byCalendar.get(key);
      if (!entry) {
        entry = {
          p6ObjectId: key,
          title: calendar?.title || '',
          assumedWorkWeek: calendar?.assumedWorkWeek || null,
          total: 0,
          mismatched: 0
        };
        check.byCalendar.set(key, entry);
      }
      entry.total += 1;
      check.compared += 1;

      const importedHours = range.duration.weighting;
      if (Math.abs(importedHours - p6Hours) <= DURATION_CHECK_TOLERANCE_HOURS) {
        check.matched += 1;
        return;
      }
      check.mismatched += 1;
      entry.mismatched += 1;
      if (check.samples.length < 5) {
        check.samples.push({
          code: childText(activity, 'Id') || '',
          title: childText(activity, 'Name') || '',
          p6Hours,
          importedHours,
          calendarTitle: entry.title
        });
      }
    }

    buildHierarchy() {
      // Update all parentTaskIds with new IDs
      this.flatTasks.forEach((task) => {
        if (task.parentTaskId) {
          task.parentTaskId = this.idMapping.get(task.parentTaskId) || null;
        }
      });

      // Group tasks by parent in one pass. A task whose ParentObjectId is
      // missing from the file was remapped to null above and becomes a root;
      // an undefined/empty parent id groups under a key assemble() never
      // visits and is dropped — both exactly as the previous per-parent scan
      // behaved.
      const childrenByParentId = new Map();
      this.flatTasks.forEach(task => {
        let group = childrenByParentId.get(task.parentTaskId);
        if (!group) {
          group = [];
          childrenByParentId.set(task.parentTaskId, group);
        }
        group.push(task);
      });

      const compareSiblings = (a, b) => {
        // Always put WBS nodes before activities
        if (a.type !== b.type) {
          return a.type === 'wbs' ? -1 : 1;
        }

        // For WBS nodes, sort by sequenceNumber if available
        if (a.sequenceNumber !== undefined && b.sequenceNumber !== undefined) {
          return a.sequenceNumber - b.sequenceNumber;
        }

        // Fall back to code sorting if no sequence numbers
        // Use natural sort to handle numeric parts correctly (e.g., 1,2,10 instead of 1,10,2)
        const aCode = a.code || '';
        const bCode = b.code || '';
        return aCode.localeCompare(bCode, undefined, { numeric: true, sensitivity: 'base' });
      };

      const assemble = (parentTaskId, parentWbs) => {
        const children = childrenByParentId.get(parentTaskId) || [];
        children.sort(compareSiblings);
        // Assign sort property starting from 0 without gaps
        children.forEach((task, index) => {
          task.sort = index;
          // wbs is the path of codes from root to task — the same shape the
          // API returns, so a re-import diff can compare it directly.
          task.wbs = [...parentWbs, String(task.code ?? '')];
          task.children = assemble(task.id, task.wbs);

          // The API's childTaskIds lists every direct child (WBS nodes
          // included), so derive it from the finished children rather than
          // collecting activities only.
          if (task.childTaskIds) {
            task.childTaskIds = task.children.map(child => child.id);
          }
        });
        return children;
      };

      const rootTasks = assemble(null, []);
      // The API represents "no parent" as an empty string; match it so a
      // re-import diff doesn't flag every root task.
      rootTasks.forEach(task => {
        if (task.parentTaskId == null) task.parentTaskId = '';
      });
      return rootTasks;
    }

    // Task ids are derived from schedule id + P6 node type + P6 ObjectId, so
    // re-importing the same XML into the same schedule reproduces the same
    // ids and the diff shows updates rather than delete + recreate. The node
    // type is part of the seed because P6 ObjectId sequences are per-table:
    // a WBS node and an Activity can share the same numeric ObjectId.
    generateNewId(nodeType, p6ObjectId) {
      return this.idGenerator.generate(
        `${this.selectedSchedule.id}|${nodeType}|${p6ObjectId}`
      );
    }

    // Same published-calendar resolution the commit walk uses
    // (ScheduleImporter.applyEndDateToTimeRange): the range's calendarId,
    // falling back to the current revision of the same version family.
    resolveWalkCalendar(timeRange) {
      return resolvePublishedCalendar(this.scheduleStore.getCalendars, timeRange) || undefined;
    }

    /**
     * Resolve the published RDrive calendar matching the P6 calendar an
     * activity references, when that P6 calendar has already been imported
     * into the project (matched by deterministic code, like CalendarImporter's
     * create-vs-update). Null when the activity has no calendar link or the
     * calendar hasn't been imported yet.
     */
    resolveP6TaskCalendar(p6CalendarObjectId) {
      if (!p6CalendarObjectId) return null;
      const p6Calendar = (this.calendars || []).find(c => c.p6ObjectId === p6CalendarObjectId);
      return matchP6CalendarByCode(p6Calendar, this.scheduleStore.getCalendars || []);
    }

    getTimeRanges(existingTask, startDate = null, endDate = null, p6CalendarObjectId = null) {
      // Calendar precedence: the activity's own (already-imported) P6 calendar,
      // then the calendar stored on the existing task (set by a previous
      // import's re-link), then the schedule's calendar mapping. Preferring the
      // mapping here would flip previously re-linked tasks back to the default
      // on every re-import.
      const p6MatchedCalendar = this.resolveP6TaskCalendar(p6CalendarObjectId);
      // The activity's P6 calendar as parsed from this file — the calendar
      // its Start/Finish were scheduled on — drives the working-time
      // duration even before that calendar is imported (the import publishes
      // the same intervals, so the committed weighting already matches).
      const p6Calendar = (p6CalendarObjectId && this.calendarsByP6Id?.get(p6CalendarObjectId)) || null;
      const durationCalendar = (timeRange) => p6Calendar || this.resolveWalkCalendar(timeRange) || null;
      // P6 dates are the source of truth: an updated export must move existing
      // tasks, and both are committed verbatim — the commit stores endDate as
      // sent and the API's publish-time recalculation skips keyed (P6) tasks,
      // so the stored range round-trips the file exactly. Everything else on
      // the stored timeRange (progress, titles, subtask links) is preserved.
      const useXmlDates = !!(startDate && endDate);

      if (existingTask && existingTask.timeRanges) {
        return existingTask.timeRanges.map(timeRange => {
          const { durationInfo: _legacy, ...rest } = timeRange;
          const isNone = timeRange.timeRangeTemplateType === 'none';
          const calendarMapping = this.excelUtils.getCalendarForTimeRange(this.selectedSchedule, timeRange.timeRangeTemplateId);
          const p6CalendarId = !isNone ? p6MatchedCalendar?.id : null;
          const calendarId = p6CalendarId
            || timeRange.calendarId
            || this.excelUtils.resolveTimeRangeCalendarId(calendarMapping.calendarId, timeRange.timeRangeTemplateType);
          const calendarVersionReference = p6CalendarId
            ? (p6MatchedCalendar.versionReference || null)
            : (timeRange.calendarId
              ? timeRange.calendarVersionReference || null
              : calendarMapping.calendarVersionReference || null);
          const storedDur = (timeRange.duration && timeRange.duration.value !== undefined)
            ? timeRange.duration
            : _legacy
              ? { value: _legacy.duration, unit: _legacy.durationUnit || 'day', weighting: _legacy.weighting }
              : null;
          const result = {
            ...rest,
            startDate: useXmlDates ? parseP6Date(startDate).toISOString() : timeRange.startDate,
            duration: useXmlDates
              ? this.calculateDuration(startDate, endDate, durationCalendar({ calendarId, calendarVersionReference }))
              : storedDur,
            calendarId,
            calendarVersionReference,
            progress: timeRange.progress,
            timeRangeTemplateId: timeRange.timeRangeTemplateId,
            timeRangeTemplateType: timeRange.timeRangeTemplateType
          };
          if (useXmlDates) {
            result.endDate = XMLTaskParser.p6EndDate(startDate, endDate);
          }
          return result;
        });
      }

      const scheduleTemplatesObj = this.scheduleStore.getScheduleTemplatesObj;
      const scheduleTemplate = scheduleTemplatesObj[this.selectedSchedule.scheduleTemplateId]?.[0];

      if (!scheduleTemplate) {
        return [];
      }

      return scheduleTemplate.timeRangeTemplates.map(template => {
        // Un-imported P6 calendar assignments are not sent: the API only
        // accepts calendar ids that already exist in the project. Calendars
        // from a previous import of this file DO exist and are matched by
        // code; the rest are re-linked after the calendar import dialog.
        const isNone = template.type === 'none';
        const calendarMapping = this.excelUtils.getCalendarForTimeRange(this.selectedSchedule, template.id);
        const p6CalendarId = !isNone ? p6MatchedCalendar?.id : null;
        const calendarId = p6CalendarId
          || this.excelUtils.resolveTimeRangeCalendarId(calendarMapping.calendarId, template.type);
        const calendarVersionReference = p6CalendarId
          ? (p6MatchedCalendar.versionReference || null)
          : (calendarMapping.calendarVersionReference || null);

        const duration = useXmlDates
          ? this.calculateDuration(startDate, endDate, durationCalendar({ calendarId, calendarVersionReference }))
          : { value: 1, unit: 'day', weighting: XMLTaskParser.DEFAULT_HOURS_PER_DAY };

        const result = {
          timeRangeTemplateId: template.id,
          timeRangeTemplateType: template.type,
          // Send an absolute instant, not the naive P6 string: the backend
          // would interpret the naive form in its own timezone, which the
          // browser can't be assumed to share.
          startDate: startDate ? parseP6Date(startDate).toISOString() : null,
          duration,
          calendarId,
          calendarVersionReference,
          progress: 0
        };
        if (useXmlDates) {
          result.endDate = XMLTaskParser.p6EndDate(startDate, endDate);
        }
        return result;
      });
    }
}
